/**
 * WizardStepBasics (Step 1)
 *
 * Collects basic project information:
 * - Project name (optional)
 * - Project category (new construction, renovation, etc.)
 * - Property type (residential/commercial)
 * - Address with jurisdiction lookup
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, AlertTriangle, Info } from 'lucide-react';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { useLookupJurisdiction, useSupportedStates, useCountiesByState, type County } from '@/hooks/useCompliance';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WizardBasicsData, ProjectCategory, PropertyType, ContractorRole } from '@/types/wizard';
import { CONTRACTOR_ROLES } from '@/types/wizard';

interface WizardStepBasicsProps {
  data: WizardBasicsData;
  onUpdate: (data: Partial<WizardBasicsData>) => void;
}

const PROJECT_CATEGORIES: { value: ProjectCategory; label: string; description: string }[] = [
  { value: 'new_construction', label: 'New Construction', description: 'Building from the ground up' },
  { value: 'renovation', label: 'Renovation / Remodel', description: 'Upgrading or changing existing space' },
  { value: 'repair', label: 'Repair / Replacement', description: 'Fixing or replacing existing systems' },
  { value: 'addition', label: 'Addition', description: 'Adding to an existing structure' },
  { value: 'tenant_improvement', label: 'Tenant Improvement', description: 'Commercial space customization' },
];

const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
];

export function WizardStepBasics({ data, onUpdate }: WizardStepBasicsProps) {
  const [streetAddress, setStreetAddress] = useState(data.address?.street || '');
  const [city, setCity] = useState(data.address?.city || '');
  const [zip, setZip] = useState(data.address?.zip || '');
  const [selectedState, setSelectedState] = useState(data.address?.state || '');
  const [selectedCounty, setSelectedCounty] = useState(data.address?.county || '');

  const { data: states = [], isLoading: statesLoading } = useSupportedStates();
  const { data: counties = [], isLoading: countiesLoading } = useCountiesByState(selectedState || null);
  const lookupJurisdiction = useLookupJurisdiction();

  // Reset county when state changes
  const handleStateChange = (stateCode: string) => {
    setSelectedState(stateCode);
    setSelectedCounty('');
    // Clear jurisdiction when state changes
    onUpdate({
      address: {
        street: streetAddress,
        city: city,
        state: stateCode,
        zip: zip,
        county: '',
        formatted: '',
      },
      jurisdictionId: undefined,
      jurisdictionCode: undefined,
      jurisdictionName: undefined,
    });
  };

  // Use ref to avoid infinite loops with mutation in useCallback
  const lookupRef = useRef(lookupJurisdiction);
  lookupRef.current = lookupJurisdiction;

  // Track the last lookup key to prevent duplicate calls
  const lastLookupKey = useRef<string>('');

  // Lookup jurisdiction when address changes
  const handleAddressLookup = useCallback(async () => {
    if (!streetAddress || !city || !selectedCounty || !selectedState) return;

    // Create a lookup key to prevent duplicate calls
    const lookupKey = `${streetAddress}-${city}-${selectedCounty}-${selectedState}`;
    if (lookupKey === lastLookupKey.current) return;
    lastLookupKey.current = lookupKey;

    const fullAddress = `${streetAddress}, ${city}, ${selectedState} ${zip}`.trim();

    try {
      const result = await lookupRef.current.mutateAsync({
        address: fullAddress,
        county: selectedCounty,
        city: city,
        state: selectedState,
      });

      if (result.jurisdiction) {
        onUpdate({
          address: {
            street: streetAddress,
            city: city,
            state: selectedState,
            zip: zip,
            county: selectedCounty,
            formatted: fullAddress,
          },
          jurisdictionId: result.jurisdiction.id,
          jurisdictionCode: result.jurisdiction.code,
          jurisdictionName: result.jurisdiction.ahjName,
          isHvhz: result.jurisdiction.isHvhz || false,
          isFloodZone: result.jurisdiction.isFloodZone || false,
          isCoastalZone: result.jurisdiction.isCoastalZone || false,
        });
      }
    } catch (error) {
      console.error('Jurisdiction lookup failed:', error);
    }
  }, [streetAddress, city, zip, selectedCounty, selectedState, onUpdate]);

  // Trigger lookup when county or state changes (only when all required fields are present)
  useEffect(() => {
    if (streetAddress && city && selectedCounty && selectedState) {
      const timeoutId = setTimeout(handleAddressLookup, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedCounty, selectedState, streetAddress, city, handleAddressLookup]);

  return (
    <div className="space-y-8">
      {/* Required fields note */}
      <p className="text-xs text-muted-foreground">
        <span className="text-red-500">*</span> Required fields
      </p>

      {/* Project Name */}
      <div className="space-y-2">
        <Label htmlFor="projectName">Project Name (optional)</Label>
        <Input
          id="projectName"
          placeholder="e.g., Kitchen Remodel, New Pool House"
          value={data.projectName || ''}
          onChange={(e) => onUpdate({ projectName: e.target.value })}
        />
        <p className="text-sm text-muted-foreground">
          A name to help you identify this project
        </p>
      </div>

      {/* Project Category */}
      <div className="space-y-3">
        <Label>
          What type of project is this?
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <RadioGroup
          value={data.category || ''}
          onValueChange={(value) => onUpdate({ category: value as ProjectCategory })}
        >
          <div className="grid gap-3">
            {PROJECT_CATEGORIES.map((category) => (
              <Label
                key={category.value}
                htmlFor={category.value}
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
              >
                <RadioGroupItem value={category.value} id={category.value} />
                <div className="flex-1">
                  <span className="font-medium">{category.label}</span>
                  <p className="text-sm text-muted-foreground">{category.description}</p>
                </div>
              </Label>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Property Type */}
      <div className="space-y-3">
        <Label>
          Is this residential or commercial?
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <RadioGroup
          value={data.propertyType || ''}
          onValueChange={(value) => onUpdate({ propertyType: value as PropertyType })}
          className="flex gap-4"
        >
          {PROPERTY_TYPES.map((type) => (
            <Label
              key={type.value}
              htmlFor={`property-${type.value}`}
              className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 flex-1 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
            >
              <RadioGroupItem value={type.value} id={`property-${type.value}`} />
              <span className="font-medium">{type.label}</span>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Contractor Role */}
      <div className="space-y-3">
        <Label>
          What is your role on this project?
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <p className="text-sm text-muted-foreground">
          This determines who is responsible for pulling permits
        </p>
        <RadioGroup
          value={data.contractorRole || ''}
          onValueChange={(value) => onUpdate({ contractorRole: value as ContractorRole })}
        >
          <div className="grid gap-3">
            {CONTRACTOR_ROLES.map((role) => (
              <Label
                key={role.code}
                htmlFor={`role-${role.code}`}
                className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
              >
                <RadioGroupItem value={role.code} id={`role-${role.code}`} className="mt-0.5" />
                <div className="flex-1 space-y-1">
                  <span className="font-medium">{role.name}</span>
                  <p className="text-sm text-muted-foreground">{role.description}</p>
                  {data.contractorRole === role.code && (
                    <div className="mt-2 p-2 rounded bg-muted/50 text-xs">
                      <p className="font-medium text-foreground">Permit Responsibility:</p>
                      <p className="text-muted-foreground">{role.permitResponsibility}</p>
                    </div>
                  )}
                </div>
              </Label>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <Label>
          Where is the project located?
          <span className="text-red-500 ml-1">*</span>
        </Label>

        <div className="grid gap-4">
          <div>
            <Label htmlFor="street" className="text-sm text-muted-foreground">
              Street Address
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="street"
              placeholder="123 Main Street"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              onBlur={handleAddressLookup}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city" className="text-sm text-muted-foreground">
                City
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="city"
                placeholder="Jacksonville"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onBlur={handleAddressLookup}
              />
            </div>
            <div>
              <Label htmlFor="zip" className="text-sm text-muted-foreground">
                ZIP Code
              </Label>
              <Input
                id="zip"
                placeholder="32205"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="state" className="text-sm text-muted-foreground">
                State
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select
                value={selectedState}
                onValueChange={handleStateChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={statesLoading ? "Loading..." : "Select state"} />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {states.map((state) => (
                    <SelectItem key={state.id} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="county" className="text-sm text-muted-foreground">
                County
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select
                value={selectedCounty}
                onValueChange={(value) => setSelectedCounty(value)}
                disabled={!selectedState}
              >
                <SelectTrigger>
                  <SelectValue placeholder={!selectedState ? "Select state first" : countiesLoading ? "Loading..." : "Select county"} />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {counties.map((county: County) => (
                    <SelectItem key={county.id} value={county.name}>
                      {county.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Jurisdiction Result */}
        {lookupJurisdiction.isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Looking up jurisdiction...
          </div>
        )}

        {data.jurisdictionId && (
          <Card className="border-border bg-muted/40 dark:bg-muted/20">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-muted">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Jurisdiction Detected</span>
                      <ConfidenceBadge
                        level="high"
                        source="Address geocoding"
                        showLabel={true}
                      />
                    </div>
                  </div>
                  <p className="text-sm font-medium mt-1">{data.jurisdictionName}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Info className="h-3 w-3" />
                    Auto-detected from: {data.address?.county} County, {data.address?.state || selectedState}
                  </p>

                  {/* Special zone indicators */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {data.isHvhz && (
                      <Badge variant="outline" className="text-xs border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                        <AlertTriangle className="h-3 w-3 mr-1 text-amber-500" />
                        High Velocity Hurricane Zone
                      </Badge>
                    )}
                    {data.isFloodZone && (
                      <Badge variant="outline" className="text-xs">
                        Flood Zone
                      </Badge>
                    )}
                    {data.isCoastalZone && (
                      <Badge variant="outline" className="text-xs">
                        Coastal Zone
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
